
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  element: JSX.Element;
  requiredRole: 'client' | 'practitioner' | 'admin' | null;
}

const ProtectedRoute = ({ element, requiredRole }: ProtectedRouteProps) => {
  const { user, isLoading, activeRole, roleReady } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }
  
  if (!user) {
    let redirectPath = '/';
    if (requiredRole === 'client') redirectPath = '/client-auth';
    else if (requiredRole === 'practitioner') redirectPath = '/practitioner-auth';
    else if (requiredRole === 'admin') redirectPath = '/admin';
    
    // Preserve current path to enable proper redirect after auth
    const currentPath = window.location.pathname;
    
    // Wait for role to be ready before making any redirect decisions
    if (!roleReady) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      );
    }
    
    // Only redirect if we're not already on an auth page or special pages
    if (currentPath !== '/booking-success' && 
        !currentPath.includes('auth') && 
        currentPath !== '/') {
      console.log(`ProtectedRoute: Redirecting unauthenticated user from ${currentPath} to ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
    }
    
    // For booking-success page, redirect to home if not authenticated
    if (currentPath === '/booking-success') {
      return <Navigate to="/" replace />;
    }
    
    // For auth pages and home, don't redirect
    return <Navigate to={redirectPath} replace />;
  }
  
  if (requiredRole === 'admin' && activeRole !== 'admin') {
    return <Navigate to="/admin" replace />;
  }

  // Enforce role-specific access for client and practitioner
  if (requiredRole && requiredRole !== 'admin' && activeRole !== requiredRole) {
    // Wait for role to be properly determined before redirecting
    if (!roleReady || activeRole === null) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      );
    }
    
    console.log(`ProtectedRoute: Role mismatch - required: ${requiredRole}, active: ${activeRole}`);
    
    // For role mismatches, redirect to homepage instead of other dashboards
    // This prevents admin users from being bounced between dashboards
    return <Navigate to="/" replace />;
  }
  
  return element;
};

export default ProtectedRoute;

